const GEONAMES_USERNAME = "dance30j";
const GEONAMES_BASE_URL = "https://secure.geonames.org";
const GEONAMES_MIN_QUERY_LEN = 2;
const GEONAMES_CACHE_TTL_MS = 5 * 60 * 1000;
const GEONAMES_AUTH_COOLDOWN_MS = 10 * 60 * 1000;
const GEONAMES_RATE_COOLDOWN_MS = 60 * 1000;

/** Shared debounce before any location search request (GeoNames, venue suggest). */
export const SEARCH_DEBOUNCE_MS = 300;

let geonamesDisabledUntil = 0;
const geonamesCache = new Map();

function normalizeCountry(raw) {
  if (!raw) return null;
  const countryCode = raw.countryCode || raw.country_code || raw.isoAlpha2 || "";
  const countryName = raw.countryName || raw.country_name || raw.name || "";
  if (!countryCode || !countryName) return null;
  return {
    countryCode,
    country_code: countryCode,
    countryName,
    country_name: countryName,
    name: countryName,
  };
}

function normalizeCity(raw) {
  if (!raw) return null;
  const name = raw.name || raw.toponymName || "";
  if (!name) return null;
  const geoId = raw.geoId || raw.geonameId || raw.geo_id || null;
  const lat = raw.lat != null ? Number(raw.lat) : raw.latitude != null ? Number(raw.latitude) : null;
  const lng = raw.lng != null ? Number(raw.lng) : raw.longitude != null ? Number(raw.longitude) : null;
  const countryCode = raw.countryCode || raw.country_code || "";
  const countryName = raw.countryName || raw.country_name || "";
  return {
    name,
    geoId: geoId != null ? String(geoId) : null,
    geo_id: geoId != null ? String(geoId) : null,
    lat: Number.isFinite(lat) ? lat : null,
    lng: Number.isFinite(lng) ? lng : null,
    countryCode,
    country_code: countryCode,
    countryName,
    country_name: countryName,
    state: raw.adminName1 || raw.state || null,
    flag: raw.flag || null,
  };
}

function disableGeoNames(ms) {
  geonamesDisabledUntil = Math.max(geonamesDisabledUntil, Date.now() + ms);
}

function geonamesSearchCacheKey(path, params) {
  const sorted = Object.keys(params || {})
    .sort()
    .map((k) => `${k}=${params[k]}`)
    .join("&");
  return `${path}?${sorted}`;
}

function readGeoNamesCache(key) {
  const hit = geonamesCache.get(key);
  if (!hit) return undefined;
  if (Date.now() - hit.at > GEONAMES_CACHE_TTL_MS) {
    geonamesCache.delete(key);
    return undefined;
  }
  return hit.data;
}

function writeGeoNamesCache(key, data) {
  geonamesCache.set(key, { at: Date.now(), data });
}

/** GeoNames call — returns null on failure. Never throws. Trips cooldown on auth/rate errors. */
async function geonamesGet(path, params) {
  if (Date.now() < geonamesDisabledUntil) return null;

  const cacheKey = geonamesSearchCacheKey(path, params);
  const cached = readGeoNamesCache(cacheKey);
  if (cached !== undefined) return cached;

  try {
    const url = new URL(`${GEONAMES_BASE_URL}${path}`);
    url.searchParams.set("username", GEONAMES_USERNAME);
    for (const [key, value] of Object.entries(params || {})) {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, value);
      }
    }
    const response = await fetch(url.toString());
    if (!response.ok) {
      disableGeoNames(GEONAMES_RATE_COOLDOWN_MS);
      return null;
    }
    const data = await response.json();
    if (data?.status?.message) {
      const code = Number(data.status.value);
      if (code === 10) disableGeoNames(GEONAMES_AUTH_COOLDOWN_MS);
      else if (code === 18 || code === 19 || code === 20) disableGeoNames(GEONAMES_RATE_COOLDOWN_MS);
      else if (code === 15) {
        writeGeoNamesCache(cacheKey, { geonames: [] });
        return { geonames: [] };
      }
      return null;
    }
    writeGeoNamesCache(cacheKey, data);
    return data;
  } catch {
    disableGeoNames(GEONAMES_RATE_COOLDOWN_MS);
    return null;
  }
}

async function fetchGeoNamesCities(query, countryCode, limit) {
  const trimmed = query.trim();
  if (trimmed.length < GEONAMES_MIN_QUERY_LEN) return [];

  const data = await geonamesGet("/searchJSON", {
    q: trimmed,
    country: countryCode || undefined,
    featureClass: "P",
    maxRows: limit,
    orderby: "population",
    style: "FULL",
    isNameRequired: true,
  });
  return (data?.geonames || []).map(normalizeCity).filter(Boolean);
}

let countriesCache = null;

export async function searchCountries(query = "", { limit = 8 } = {}) {
  if (!countriesCache) {
    const data = await geonamesGet("/countryInfoJSON", {});
    countriesCache = (data?.geonames || []).map(normalizeCountry).filter(Boolean);
  }
  const needle = query.trim().toLowerCase();
  const pool = needle
    ? countriesCache.filter((c) => {
        const haystack = `${c.countryName} ${c.countryCode}`.toLowerCase();
        return haystack.includes(needle);
      })
    : countriesCache;
  return pool.slice(0, limit);
}

/** City suggestions from GeoNames. */
export async function searchCities({ query = "", countryCode, limit = 8 } = {}) {
  const trimmed = query.trim();
  if (trimmed.length < GEONAMES_MIN_QUERY_LEN) return [];

  const cities = await fetchGeoNamesCities(trimmed, countryCode, limit);
  return cities.slice(0, limit);
}

export function normalizeLocationCity(raw) {
  return normalizeCity(raw);
}

/** Stable GeoNames city ID from a location object (geoId / geo_id). */
export function cityGeoId(city) {
  if (!city) return null;
  const id = city.geoId ?? city.geo_id ?? city.geonameId;
  return id != null ? String(id) : null;
}
